import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, ArrowRight } from 'lucide-react';

const utilityLinks = [
  { label: 'About Us', href: '/about' },
  { label: 'Warranty Policy', href: '/warranty' },
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Terms & Conditions', href: '/terms' },
];

const mainNavLinks = [
  { label: 'Home', href: '/' },
  {
    label: 'Services',
    href: '#',
    children: [
      { label: 'All Services', href: '/services/overheating-power', tag: 'View All' },
      { label: 'DLP Chip Repair', href: '/services/white-dots-dmd', tag: 'DLP' },
      { label: 'Power & Overheating', href: '/services/overheating-power', tag: 'Thermal' },
      { label: 'Display Discolouration', href: '/services/display-discolouration', tag: 'LCD' },
      { label: 'Laser & Lamp Service', href: '/services/laser-lamp', tag: 'Light' },
    ],
  },
  { label: 'Extended Warranty', href: '/extended-warranty', tag: 'NEW' },
  { label: 'Areas', href: '/locations' },
  {
    label: 'Brands',
    href: '#',
    children: [
      { label: 'Epson', href: '/brands/epson', tag: '3LCD' },
      { label: 'Optoma', href: '/brands/optoma', tag: 'DLP' },
      { label: 'BenQ', href: '/brands/benq', tag: 'DLP' },
      { label: 'Sony', href: '/brands/sony', tag: 'SXRD' },
      { label: 'Panasonic', href: '/brands/panasonic', tag: '3LCD/Laser' },
      { label: 'ViewSonic', href: '/brands/viewsonic', tag: 'DLP' },
      { label: 'NEC', href: '/brands/nec', tag: '3LCD' },
      { label: 'JVC', href: '/brands/jvc', tag: 'D-ILA' },
      { label: 'Samsung', href: '/brands/samsung', tag: 'Laser' },
      { label: 'Acer', href: '/brands/acer', tag: 'DLP' },
      { label: 'Hitachi', href: '/brands/hitachi', tag: '3LCD' },
      { label: 'Promethean', href: '/brands/promethean', tag: 'Education' },
      { label: 'Smartboard', href: '/brands/smartboard', tag: 'Education' },
      { label: 'SIM2', href: '/brands/sim2', tag: 'Cinema' },
    ],
    ustChildren: [
      { label: 'Ultra Short Throw', href: '#', tag: 'HEADER' },
      { label: 'Hisense', href: '/brands/hisense', tag: 'UST' },
      { label: 'LG', href: '/brands/lg', tag: 'UST' },
      { label: 'Xiaomi', href: '/brands/xiaomi', tag: 'UST' },
      { label: 'Wemax', href: '/brands/wemax', tag: 'UST' },
      { label: 'Leica Cine', href: '/brands/leica-cine', tag: 'UST' },
      { label: 'Formovie', href: '/brands/formovie', tag: 'UST' },
      { label: 'VAVA', href: '/brands/vava', tag: 'UST' },
      { label: 'AWOL Vision', href: '/brands/awol-vision', tag: 'UST' },
      { label: 'Casiris', href: '/brands/casiris', tag: 'UST' },
      { label: 'Nexigo', href: '/brands/nexigo', tag: 'UST' },
      { label: 'Ultimea', href: '/brands/ultimea', tag: 'UST' },
    ],
    commercialChildren: [
      { label: 'Commercial / Install', href: '#', tag: 'HEADER' },
      { label: 'Christie', href: '/brands/christie', tag: 'Pro' },
      { label: 'Sanyo', href: '/brands/sanyo', tag: 'PLC' },
      { label: 'Vivitek', href: '/brands/vivitek', tag: 'DLP' },
      { label: 'DHN', href: '/brands/dhn', tag: 'Laser' },
      { label: 'Barco', href: '/brands/barco', tag: 'Pro' },
      { label: 'Digital Projection', href: '/brands/digital-projection', tag: 'Pro' },
    ],
  },
  { label: 'About Us', href: '/about' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const handleEnter = (label: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenDropdown(label);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Utility Bar */}
      <div className="bg-[#003D80] h-9 flex items-center z-50 relative">
        <div className="container-custom flex items-center justify-between w-full">
          <div className="hidden md:flex items-center gap-4">
            {utilityLinks.map((link) => (
              <Link key={link.href} to={link.href} className="text-[11px] text-white/80 hover:text-white/90 transition-colors">
                {link.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <a href="tel:+443330064326" className="text-[11px] text-white/80 hover:text-white/90 transition-colors flex items-center gap-1">
              <span className="text-white/30">UK:</span> 0333 006 4326
            </a>
            <a href="tel:+35315827344" className="text-[11px] text-white/80 hover:text-white/90 transition-colors hidden sm:flex items-center gap-1">
              <span className="text-white/30">IE:</span> +353 1 582 7344
            </a>
          </div>
        </div>
      </div>

      {/* Main Nav */}
      <header className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? 'bg-[#0057B7]/95 backdrop-blur-md shadow-lg' : 'bg-[#0057B7]'}`}>
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 lg:h-18">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#FFDD00] text-[#0057B7]">
                <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-white">
                  <rect x="2" y="6" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
                  <circle cx="14" cy="11" r="3" stroke="currentColor" strokeWidth="2" />
                  <path d="M18 9l4-2M18 13l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <div className="leading-tight">
                <span className="block text-[15px] font-heading font-extrabold text-white tracking-tight">PROJECTOR REPAIR</span>
                <span className="block text-[11px] font-label text-[#FFDD00] tracking-[0.15em] -mt-0.5">LONDON</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {mainNavLinks.map((link) => (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={link.children ? () => handleEnter(link.label) : undefined}
                  onMouseLeave={link.children ? handleLeave : undefined}
                >
                  {link.children ? (
                    <>
                      <button
                        className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          (link.label === 'Services' && isActive('/services/')) ||
                          (link.label === 'Brands' && isActive('/brands/'))
                            ? 'text-[#FFDD00]' : 'text-white/80 hover:text-white'
                        }`}
                      >
                        {link.label}
                        <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === link.label ? 'rotate-180' : ''}`} />
                      </button>
                      {openDropdown === link.label && (
                        <div className="absolute top-full left-0 mt-1 flex">
                          {/* Main brands column */}
                          <div className="w-56 bg-[#3d3834] border border-white/10 rounded-xl shadow-2xl py-2 z-50">
                            {link.children.map((child) => (
                              <Link
                                key={child.href}
                                to={child.href}
                                className={`flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                                  isActive(child.href) ? 'text-[#0057B7] bg-[#FFDD00]/20' : 'text-white/80 hover:text-white hover:bg-white/5'
                                }`}
                              >
                                <span>{child.label}</span>
                                <span className="text-[10px] px-1.5 py-0.5 bg-white/5 rounded text-white/30">{child.tag}</span>
                              </Link>
                            ))}
                          </div>
                          {/* UST column */}
                          {'ustChildren' in link && link.ustChildren && (
                            <div className="w-56 bg-[#3d3834] border border-white/10 border-l-0 rounded-r-xl shadow-2xl py-2 z-50 ml-0">
                              {link.ustChildren.map((child, idx) => (
                                child.tag === 'HEADER' ? (
                                  <div key={idx} className="px-4 py-2 text-[10px] font-label text-[#FFDD00] uppercase tracking-widest">{child.label}</div>
                                ) : (
                                  <Link
                                    key={child.href}
                                    to={child.href}
                                    className={`flex items-center justify-between px-4 py-2 text-sm transition-colors ${
                                      isActive(child.href) ? 'text-[#0057B7] bg-[#FFDD00]/20' : 'text-white/80 hover:text-white hover:bg-white/5'
                                    }`}
                                  >
                                    <span>{child.label}</span>
                                    <span className="text-[10px] px-1.5 py-0.5 bg-white/5 rounded text-white/30">{child.tag}</span>
                                  </Link>
                                )
                              ))}
                            </div>
                          )}
                          {/* Commercial column */}
                          {'commercialChildren' in link && link.commercialChildren && (
                            <div className="w-56 bg-[#3d3834] border border-white/10 border-l-0 rounded-r-xl shadow-2xl py-2 z-50 ml-0">
                              {link.commercialChildren.map((child, idx) => (
                                child.tag === 'HEADER' ? (
                                  <div key={idx} className="px-4 py-2 text-[10px] font-label text-[#FFDD00] uppercase tracking-widest">{child.label}</div>
                                ) : (
                                  <Link
                                    key={child.href}
                                    to={child.href}
                                    className={`flex items-center justify-between px-4 py-2 text-sm transition-colors ${
                                      isActive(child.href) ? 'text-[#0057B7] bg-[#FFDD00]/20' : 'text-white/80 hover:text-white hover:bg-white/5'
                                    }`}
                                  >
                                    <span>{child.label}</span>
                                    <span className="text-[10px] px-1.5 py-0.5 bg-white/5 rounded text-white/30">{child.tag}</span>
                                  </Link>
                                )
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={link.href}
                      className={`block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive(link.href) ? 'text-[#FFDD00]' : 'text-white/80 hover:text-white'
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA + Mobile Menu */}
            <div className="flex items-center gap-3">
              <Link to="/repair-enquiry" className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-[#FFDD00] text-[#0057B7] rounded-lg text-sm font-semibold hover:bg-[#C9A800] transition-all hover:scale-[1.02]">
                Repair Enquiry <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-3 rounded-lg text-white/80 hover:text-white hover:bg-white/5 transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center"
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
        <div className="nav-divider" />
      </header>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 top-[73px] z-30 bg-[#0057B7] overflow-y-auto">
          <nav className="flex flex-col gap-1 p-6">
            <Link to="/" className={`min-h-[48px] flex items-center py-3 text-lg font-heading font-semibold border-b border-white/5 ${isActive('/') ? 'text-[#FFDD00]' : 'text-white'}`}>Home</Link>
            <div className="py-2">
              <p className="text-white/40 text-xs font-label mb-2 min-h-[20px] flex items-center">Services</p>
              <div className="pl-3 flex flex-col gap-2 border-l border-white/10">
                {mainNavLinks[1].children?.map(c => <Link key={c.href} to={c.href} className="min-h-[48px] flex items-center py-2 text-sm text-white/70">{c.label}</Link>)}
              </div>
            </div>
            <div className="py-2">
              <p className="text-white/40 text-xs font-label mb-2 min-h-[20px] flex items-center">Brands</p>
              <div className="pl-3 flex flex-col gap-2 border-l border-white/10">
                {mainNavLinks[2].children?.map(c => <Link key={c.href} to={c.href} className="min-h-[48px] flex items-center py-2 text-sm text-white/70">{c.label}</Link>)}
              </div>
              <p className="text-[#FFDD00] text-[10px] font-label mt-3 mb-1 pl-3 min-h-[20px] flex items-center">ULTRA SHORT THROW</p>
              <div className="pl-3 flex flex-col gap-2 border-l border-[#FFDD00]/20">
                {'ustChildren' in mainNavLinks[2] && mainNavLinks[2].ustChildren?.filter(c => c.tag !== 'HEADER').map(c => <Link key={c.href} to={c.href} className="min-h-[48px] flex items-center py-2 text-sm text-white/70">{c.label}</Link>)}
              </div>
              <p className="text-[#FFDD00] text-[10px] font-label mt-3 mb-1 pl-3 min-h-[20px] flex items-center">COMMERCIAL / INSTALL</p>
              <div className="pl-3 flex flex-col gap-2 border-l border-[#FFDD00]/20">
                {'commercialChildren' in mainNavLinks[2] && mainNavLinks[2].commercialChildren?.filter(c => c.tag !== 'HEADER').map(c => <Link key={c.href} to={c.href} className="min-h-[48px] flex items-center py-2 text-sm text-white/70">{c.label}</Link>)}
              </div>
            </div>
            <Link to="/locations" className={`min-h-[48px] flex items-center py-3 text-lg font-heading font-semibold border-b border-white/5 ${isActive('/locations') ? 'text-[#FFDD00]' : 'text-white'}`}>Service Areas</Link>
            <Link to="/about" className={`min-h-[48px] flex items-center py-3 text-lg font-heading font-semibold border-b border-white/5 ${isActive('/about') ? 'text-[#FFDD00]' : 'text-white'}`}>About Us</Link>
            <Link to="/repair-enquiry" className={`min-h-[48px] flex items-center py-3 text-lg font-heading font-semibold border-b border-white/5 text-[#FFDD00]`}>Repair Enquiry</Link>
            <a href="tel:+443330064326" className="mt-6 flex items-center justify-center gap-2 py-3 px-4 bg-[#FFDD00] text-[#0057B7] rounded-lg font-semibold min-h-[#48px]">
              <Phone className="w-5 h-5" /> Call Now
            </a>
          </nav>
        </div>
      )}
    </>
  );
}